import { useMap } from '@vis.gl/react-google-maps'
import { Add, Minus, MyLocation } from '@watersport/ui-react-icons'
import { Button } from '@watersport/ui-react-next'
import React, { useCallback } from 'react'

import { useSearch } from 'contexts/SearchProvider'
import { cn } from 'features/common/helpers/cn'

export interface CustomMapControlsPros {
  showMyLocation?: boolean
  zoomIn?: (zoom: number) => void
  zoomOut?: (zoom: number) => void
  onClickMyLocation?: () => void
}

const CustomMapControls: React.FC<CustomMapControlsPros> = ({
  showMyLocation,
  zoomIn,
  zoomOut,
  onClickMyLocation,
}) => {
  const map = useMap()

  const { myLocation, requestLocation, showModal } = useSearch()

  const handleZoomIn = useCallback(() => {
    if (map) {
      const currentZoom = map.getZoom()
      if (currentZoom !== undefined) {
        map.setZoom(currentZoom + 1)
        if (zoomIn) zoomIn(currentZoom + 1)
      }
    }
  }, [map, zoomIn])

  const handleZoomOut = useCallback(() => {
    if (map) {
      const currentZoom = map.getZoom()
      if (currentZoom !== undefined) {
        map.setZoom(currentZoom - 1)
        if (zoomOut) zoomOut(currentZoom - 1)
      }
    }
  }, [map, zoomOut])

  const handleMyLocation = useCallback(async () => {
    if (myLocation) {
      if (map) map.setCenter(myLocation)
      if (onClickMyLocation) onClickMyLocation()
    } else {
      const status = await showModal()
      if (status) {
        requestLocation()
      }
    }
  }, [myLocation, map, onClickMyLocation, showModal, requestLocation])

  return (
    <div className="flex flex-col gap-2" data-testid="custom-map-controls">
      {showMyLocation && (
        <Button
          className={cn(
            'w-[46px] border-blue-400 rounded-lg text-black px-2 shadow-md cursor-pointer hover:text-blue-700',
          )}
          size={'l'}
          variant={'outline'}
          text={<MyLocation width={24} height={24} className="text-blue-700" />}
          onClick={handleMyLocation}
          data-testid="my-location-button"
        />
      )}
      <Button
        className={cn(
          'w-[46px] border-blue-400 rounded-lg text-black px-2 shadow-md cursor-pointer hover:text-blue-700',
        )}
        size={'l'}
        variant={'outline'}
        text={<Add width={24} height={24} className="text-blue-700" />}
        onClick={handleZoomIn}
        data-testid="zoom-in-button"
      />
      <Button
        className={cn(
          'w-[46px] border-blue-400 rounded-lg text-black px-2 shadow-md cursor-pointer hover:text-blue-700',
        )}
        size={'l'}
        variant={'outline'}
        text={<Minus width={24} height={24} className="text-blue-700" />}
        onClick={handleZoomOut}
        data-testid="zoom-out-button"
      />
    </div>
  )
}

export default CustomMapControls
